/**
 * src/components/CustomerRoleRoute.tsx — guard customer-only pages.
 *
 * Nested inside <ProtectedRoute> so unauthenticated visits hit /login.
 * An authenticated user whose Customer.role is NOT customer/both gets
 * bounced to the role-appropriate landing — drivers to /driver/active,
 * admins to /admin. Customers and dual-role ("both") users pass through.
 *
 * Three-state handling mirrors DriverRoleRoute / AdminRoute so deep-links
 * don't race the profile fetch.
 */

import { useRef } from 'react';
import { Navigate, Outlet } from 'react-router';
import { useCustomerRole } from '../hooks/useCustomerRole';
import { LoadingFallback } from './LoadingFallback';

export function CustomerRoleRoute() {
  const { role, loading } = useCustomerRole();

  // Only block on the INITIAL role resolution. useCustomerProfile (which backs
  // useCustomerRole) re-enters loading=true on every background refetch —
  // roleVersion bumps, and the customerEmail/token settling right after sign-in.
  // If we returned LoadingFallback on those, the <Outlet/> — and the ENTIRE
  // customer subtree below it, including the gated WelcomeFlow — would unmount
  // and remount on each flap, resetting WelcomeFlow's phase to null so it never
  // settles (the headless-CI onboarding stall: the flow renders, gets torn down
  // mid-sign-in, and the 60s e2e budget expires). The profile keeps its prior
  // value during a refetch, so `role` stays valid; keep the subtree mounted and
  // only re-evaluate the role redirect when the role itself changes.
  const resolvedOnceRef = useRef(false);
  if (!loading) resolvedOnceRef.current = true;
  if (loading && !resolvedOnceRef.current) return <LoadingFallback />;

  // customer, both, or no-role-yet (fresh signup) → let them in.
  if (role === 'customer' || role === 'both' || !role) {
    return <Outlet />;
  }

  // Drivers see driver pages, admins see admin pages — never the customer shell.
  if (role === 'driver') return <Navigate to="/driver/active" replace />;
  if (role === 'admin') return <Navigate to="/admin" replace />;
  return <Navigate to="/login" replace />;
}
